import Link from '@/routing/Link'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MessageSquare, MoreHorizontal, Trash2, Upload } from 'lucide-react'
import { isContextMaterialIndexingReady } from '@digital-twins/shared-types'
import { PageContent } from '@/components/layout/PageContent'
import { PageLayout } from '@/components/layout/PageLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { KnowledgeIndexingBadge } from '../KnowledgeHubPage/KnowledgeIndexingBadge'
import { formatLibraryDate } from '../KnowledgeHubPage/libraryHelpers'
import {
  knowledgeStageFailures,
  KnowledgeStageFailureNotice,
} from '../KnowledgeHubPage/KnowledgeStageFailureNotice'
import { DetailsPanel } from './DetailsPanel'
import { PreviewPane } from './PreviewPane'
import { useKnowledgeDocumentDetail } from './useKnowledgeDocumentDetail'

export default function KnowledgeDocumentPage() {
  const { t } = useTranslation('app')
  const { documentToken } = useParams<{ documentToken: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const pageParam = searchParams.get('page')
  const timeParam = searchParams.get('t')
  const initialPage = pageParam != null ? Number(pageParam) : null

  const {
    doc,
    setDoc,
    loading,
    title,
    versions,
    downloading,
    deleting,
    facetBusy,
    savingDetails,
    instructionsDraft,
    setInstructionsDraft,
    instructionsBusy,
    replacing,
    replaceInputRef,
    pagesLoading,
    preview,
    pageUnknown,
    availablePages,
    maxKnownPage,
    selectPage,
    handleDownload,
    handleDelete,
    handleSaveDetails,
    handleConfirmFacet,
    handleCorrectFacet,
    handleRejectFacet,
    handleConfirmAllFacets,
    handleSaveInstructions,
    handleReplaceFile,
  } = useKnowledgeDocumentDetail({
    documentToken,
    initialPage,
    onPageResolved: (pageNumber) => {
      if (searchParams.get('page') === String(pageNumber)) return
      const next = new URLSearchParams(searchParams)
      next.set('page', String(pageNumber))
      setSearchParams(next, { replace: true })
    },
    onDeleted: () => navigate('/knowledge'),
  })

  return (
    <PageLayout className="h-[calc(100dvh-1rem)] max-w-none gap-0 p-0">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border px-4 pt-14 pb-3 md:px-6 md:pt-6 md:pb-4 lg:px-8">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <nav
            className="text-sm text-muted-foreground"
            data-testid="knowledge-doc-breadcrumb"
          >
            <Link href="/knowledge" className="hover:text-foreground hover:underline">
              {t('library.title')}
            </Link>
          </nav>
          {doc &&
          (doc.isSimulationArtifact ||
            doc.isScannedPdf ||
            !isContextMaterialIndexingReady(doc.indexingStatus)) ? (
            <>
              <KnowledgeIndexingBadge status={doc.indexingStatus} />
              {doc.isSimulationArtifact ? (
                <Badge variant="muted" data-testid="knowledge-doc-sim-badge">
                  {t('library.type.simulationReport')}
                </Badge>
              ) : null}
              {doc.isScannedPdf ? (
                <Badge variant="warning" data-testid="knowledge-doc-scanned-badge">
                  {t('library.noSearchableText')}
                </Badge>
              ) : null}
            </>
          ) : null}
        </div>

        {doc ? (
          <div className="flex shrink-0 items-center gap-2">
            <input
              ref={replaceInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.html,.htm,.json,.mp4,.mp3,.webm,.mov,.wav,.m4a,application/pdf,video/mp4,audio/mpeg,audio/mp3"
              data-testid="knowledge-doc-replace-input"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) {
                  void handleReplaceFile(file)
                }
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  shape="pill"
                  className="h-10 w-10"
                  data-testid="knowledge-doc-more"
                  aria-label={t('library.preview.moreActions')}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl">
                <DropdownMenuItem
                  data-testid="knowledge-doc-ask"
                  onClick={() => {
                    if (!documentToken) return
                    navigate(`/threads?doc=${encodeURIComponent(documentToken)}`)
                  }}
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  {t('library.preview.askAboutThis')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="knowledge-doc-replace"
                  disabled={replacing}
                  onClick={() => replaceInputRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {replacing ? t('library.preview.replacing') : t('library.preview.replaceFile')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel data-testid="knowledge-doc-versions">
                  {t('library.preview.versionHistory')}
                </DropdownMenuLabel>
                {versions.map((version) => (
                  <DropdownMenuItem
                    key={`${version.version}-${version.createdAt}`}
                    disabled
                    className="gap-2"
                    data-testid="knowledge-doc-version-row"
                  >
                    <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium">
                      {version.version.replace(/^v?/i, 'v')}
                    </span>
                    <Text as="span" size="xs" color="muted">
                      {version.isCurrent ? t('library.preview.current') : version.label} ·{' '}
                      {formatLibraryDate(version.createdAt)}
                      {version.ownerName
                        ? ` ${t('library.preview.versionBy', { name: version.ownerName })}`
                        : doc.ownerName
                          ? ` ${t('library.preview.versionBy', { name: doc.ownerName })}`
                          : ''}
                    </Text>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  data-testid="knowledge-doc-delete"
                  disabled={deleting}
                  onClick={() => void handleDelete()}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('library.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="px-4 pt-4 md:px-6 lg:px-8">
          <Skeleton className="h-40 rounded-2xl" />
        </div>
      ) : !doc ? (
        <div
          className="mx-4 mt-4 rounded-2xl border border-card-border bg-card-inset p-8 text-center md:mx-6 lg:mx-8"
          data-testid="knowledge-doc-missing"
        >
          <Text color="muted">{t('library.preview.toastNotFound')}.</Text>
          <Button asChild variant="ghost" className="mt-4" data-testid="knowledge-doc-missing-back">
            <Link href="/knowledge">{t('library.title')}</Link>
          </Button>
        </div>
      ) : (
        <PageContent
          className="min-h-0 flex-1 gap-0"
          data-testid="knowledge-doc-detail"
        >
          <div className="shrink-0 space-y-3 px-4 pt-4 md:px-6 lg:px-8">
            <KnowledgeStageFailureNotice
              doc={doc}
              variant="detail"
              onDocumentUpdated={(updated) => setDoc(updated)}
            />

            {doc.isScannedPdf ? (
              <Text size="sm" color="muted" data-testid="knowledge-doc-scanned-banner">
                This PDF has little extractable text, so it will not appear usefully in search.
              </Text>
            ) : null}
            {doc.indexingError && knowledgeStageFailures(doc).length === 0 ? (
              <Text size="sm" className="text-destructive">
                {doc.indexingError}
              </Text>
            ) : null}
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6">
            <PreviewPane
              className="min-h-[24rem] lg:col-span-2"
              documentToken={documentToken ?? ''}
              mimeType={doc.mimeType}
              originalFilename={doc.originalFilename}
              sourceUrl={doc.sourceUrl}
              linkThumbnailUrl={doc.linkThumbnailUrl}
              title={title}
              preview={preview}
              availablePages={availablePages}
              maxKnownPage={maxKnownPage}
              pagesLoading={pagesLoading}
              pageUnknown={pageUnknown}
              startAt={timeParam}
              downloading={downloading}
              onSelectPage={(pageNumber) => void selectPage(pageNumber)}
              onDownload={() => void handleDownload()}
            />

            <DetailsPanel
              className="lg:col-span-1"
              document={doc}
              savingDetails={savingDetails}
              onSaveTitle={(next) => void handleSaveDetails({ title: next })}
              onSaveSummary={(next) => void handleSaveDetails({ summary: next })}
              facetBusy={facetBusy}
              onConfirmFacet={(facet) => void handleConfirmFacet(facet)}
              onRejectFacet={(facet) => void handleRejectFacet(facet)}
              onCorrectFacet={(facet, value) =>
                void handleCorrectFacet(facet, value)
              }
              onConfirmAllFacets={() => void handleConfirmAllFacets()}
              instructionsDraft={instructionsDraft}
              instructionsBusy={instructionsBusy}
              onInstructionsChange={setInstructionsDraft}
              onSaveInstructions={() => void handleSaveInstructions()}
            />
          </div>
        </PageContent>
      )}
    </PageLayout>
  )
}
